import axios from "axios";
import BASE_URL from "./index";

/**
 * Signed URLs for stored files.
 *
 * /preview-file and /download are loaded straight by the browser, so they
 * cannot send our session token. The API issues a short-lived signature per
 * file instead; this module fetches those, caches them, and batches requests
 * so a page showing ten photos makes one call rather than ten.
 */

const cache = new Map(); // fileId -> { preview, download, fetchedAt }
const inFlight = new Map(); // fileId -> Promise

// Grants last 15 minutes server-side. Re-fetch well before that so a link
// never expires while someone is still looking at the page.
const REFRESH_AFTER_MS = 10 * 60 * 1000;

let queue = new Set();
let queueTimer = null;
let queueResolvers = [];

const isFresh = (entry) => entry && Date.now() - entry.fetchedAt < REFRESH_AFTER_MS;

const flush = async () => {
  const fileIds = [...queue];
  const resolvers = queueResolvers;
  queue = new Set();
  queueResolvers = [];
  queueTimer = null;

  if (fileIds.length === 0) {
    resolvers.forEach((r) => r());
    return;
  }

  try {
    const res = await axios.post(`${BASE_URL}/file-access`, { fileIds });
    const urls = res?.data?.urls || {};
    const fetchedAt = Date.now();
    for (const [fileId, value] of Object.entries(urls)) {
      cache.set(fileId, {
        preview: BASE_URL + value.preview,
        download: BASE_URL + value.download,
        fetchedAt,
      });
    }
  } catch (error) {
    // Leave the cache empty; callers render their fallback rather than a
    // broken image, and the next render will try again.
    console.error("Could not fetch file links:", error?.message || error);
  } finally {
    fileIds.forEach((id) => inFlight.delete(id));
    resolvers.forEach((r) => r());
  }
};

const enqueue = (fileId) => {
  if (inFlight.has(fileId)) return inFlight.get(fileId);

  queue.add(fileId);
  const promise = new Promise((resolve) => queueResolvers.push(resolve));
  inFlight.set(fileId, promise);

  // Collect everything requested in this tick into one request.
  if (!queueTimer) queueTimer = setTimeout(flush, 0);
  return promise;
};

/**
 * Resolve signed URLs for one or more file ids.
 * @returns {Promise<Object>} map of fileId -> { preview, download }
 */
export const getFileUrls = async (fileIds) => {
  const ids = (Array.isArray(fileIds) ? fileIds : [fileIds])
    .map((id) => (id ? String(id) : ""))
    .filter((id) => /^[a-f\d]{24}$/i.test(id));

  const stale = ids.filter((id) => !isFresh(cache.get(id)));
  await Promise.all(stale.map(enqueue));

  const out = {};
  for (const id of ids) {
    const entry = cache.get(id);
    if (entry) out[id] = { preview: entry.preview, download: entry.download };
  }
  return out;
};

/** Synchronous read of an already-resolved URL, or null. */
export const peekFileUrl = (fileId, kind = "preview") => {
  const entry = cache.get(String(fileId || ""));
  return entry ? entry[kind] : null;
};
